import { useState } from 'react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { ChevronDown, ChevronRight, History, Loader2, MinusCircle, PlusCircle } from 'lucide-react';
import { toast } from 'sonner';
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { trpc } from '~/api/trpc';
import { useUserStore } from '~/stores/user-store';
import { UserRole } from '~/types/server-types/UserRole';

type CustomerBundlesTableProps = {
    customerId: number;
};

type BundleVoucher = {
    id: string;
    treatmentVariantId: string;
    treatmentVariantName: string;
    unitPriceGross: number;
    unitQuantity: number;
    usedUnits: number;
    remainingUnits: number;
    totalValueEur: number;
    remainingValueEur: number;
    sourceBillingDraftId: string;
    sourceBillingDraftNumber: string;
    sourceBillingDraftDate: Date;
    invoiceNumber: string;
    createdAt: Date;
    transactionCount: number;
};

const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
    }).format(amount);
};

// Expanded row component for transaction history
const ExpandedRowContent = ({
    voucher,
    customerId,
    isAdmin,
}: {
    voucher: BundleVoucher;
    customerId: number;
    isAdmin: boolean;
}) => {
    const [showAdjustmentForm, setShowAdjustmentForm] = useState(false);
    const [adjustmentUnits, setAdjustmentUnits] = useState<number>(0);
    const [adjustmentReason, setAdjustmentReason] = useState('');
    const trpcUtils = trpc.useUtils();

    const { data: transactions, isLoading } = trpc.customerMoney.getBundleVoucherTransactions.useQuery(
        { bundleVoucherId: voucher.id }
    );

    const adjustMutation = trpc.customerMoney.adjustBundleVoucher.useMutation({
        onSuccess: () => {
            toast.success('Gutschein wurde angepasst');
            trpcUtils.customerMoney.getCustomerBundleVouchers.invalidate({ customerId });
            trpcUtils.customerMoney.getBundleVoucherTransactions.invalidate({ bundleVoucherId: voucher.id });
            setShowAdjustmentForm(false);
            setAdjustmentUnits(0);
            setAdjustmentReason('');
        },
        onError: (error) => {
            toast.error(`Fehler: ${error.message}`);
        },
    });

    const handleAdjust = () => {
        if (adjustmentUnits === 0 || !adjustmentReason.trim()) {
            return;
        }
        adjustMutation.mutate({
            bundleVoucherId: voucher.id,
            quantityUnits: adjustmentUnits,
            reason: adjustmentReason.trim(),
        });
    };

    return (
        <div className='p-4 bg-muted/30 space-y-4'>
            <div className='flex items-center gap-2'>
                <History className='h-4 w-4' />
                <span className='text-sm font-medium'>
                    Transaktionen ({voucher.transactionCount})
                </span>
            </div>

            {isLoading ? (
                <div className='space-y-1'>
                    {[...Array(3)].map((_, i) => (
                        <Skeleton key={i} className='h-8 w-full' />
                    ))}
                </div>
            ) : transactions && transactions.length > 0 ? (
                <div className='space-y-1 max-h-40 overflow-y-auto'>
                    {transactions.map((tx) => (
                        <div
                            key={tx.id}
                            className='flex justify-between items-center text-xs py-2 px-3 bg-background rounded border'
                        >
                            <div className='flex items-center gap-2 flex-wrap'>
                                <span className={tx.quantityUnits > 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                                    {tx.quantityUnits > 0 ? '+' : ''}{tx.quantityUnits} Einheiten
                                </span>
                                <span className='text-muted-foreground'>
                                    {tx.type === 'REDEMPTION' && 'Eingelöst'}
                                    {tx.type === 'REFUND' && 'Erstattet'}
                                    {tx.type === 'ADJUSTMENT' && 'Korrektur'}
                                </span>
                                {tx.billingDraftLineItem?.invoice && (
                                    <span className='font-mono text-muted-foreground'>
                                        Rg. {tx.billingDraftLineItem.invoice.invoiceNumber}
                                    </span>
                                )}
                                {tx.description && (
                                    <span className='text-muted-foreground'>- {tx.description}</span>
                                )}
                            </div>
                            <span className='text-muted-foreground shrink-0 ml-2'>
                                {format(new Date(tx.createdAt), 'dd.MM.yyyy HH:mm', { locale: de })}
                            </span>
                        </div>
                    ))}
                </div>
            ) : (
                <p className='text-sm text-muted-foreground'>
                    Noch keine Transaktionen
                </p>
            )}

            {/* Admin adjustment section */}
            {/*
            {isAdmin && (
                <>
                    <Separator />
                    {!showAdjustmentForm ? (
                        <Button
                            variant='outline'
                            size='sm'
                            onClick={() => setShowAdjustmentForm(true)}
                        >
                            Manuell anpassen
                        </Button>
                    ) : (
                        <div className='space-y-3 p-3 border rounded-lg bg-background'>
                            <div className='space-y-2'>
                                <Label>Einheiten hinzufügen/entfernen</Label>
                                <div className='flex items-center gap-2'>
                                    <Button
                                        type='button'
                                        variant='outline'
                                        size='icon'
                                        onClick={() => setAdjustmentUnits(adjustmentUnits - 1)}
                                    >
                                        <MinusCircle className='h-4 w-4' />
                                    </Button>
                                    <Input
                                        type='number'
                                        value={adjustmentUnits}
                                        onChange={(e) => setAdjustmentUnits(parseInt(e.target.value) || 0)}
                                        className='w-20 text-center'
                                    />
                                    <Button
                                        type='button'
                                        variant='outline'
                                        size='icon'
                                        onClick={() => setAdjustmentUnits(adjustmentUnits + 1)}
                                    >
                                        <PlusCircle className='h-4 w-4' />
                                    </Button>
                                </div>
                            </div>
                            <div className='space-y-2'>
                                <Label>Begründung *</Label>
                                <Textarea
                                    value={adjustmentReason}
                                    onChange={(e) => setAdjustmentReason(e.target.value)}
                                    placeholder='Grund für die Anpassung...'
                                    rows={2}
                                />
                            </div>
                            <div className='flex gap-2'>
                                <Button
                                    variant='outline'
                                    size='sm'
                                    onClick={() => {
                                        setShowAdjustmentForm(false);
                                        setAdjustmentUnits(0);
                                        setAdjustmentReason('');
                                    }}
                                >
                                    Abbrechen
                                </Button>
                                <Button
                                    size='sm'
                                    onClick={handleAdjust}
                                    disabled={adjustmentUnits === 0 || !adjustmentReason.trim() || adjustMutation.isPending}
                                >
                                    {adjustMutation.isPending && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                                    Anpassen
                                </Button>
                            </div>
                        </div>
                    )}
                </>
            )}
            */}
        </div>
    );
};

const CustomerBundlesTable = ({ customerId }: CustomerBundlesTableProps) => {
    const { user } = useUserStore();
    const isAdmin = user?.role === UserRole.ADMIN || user?.role === UserRole.OPERATOR;

    const { data: vouchers, isLoading } = trpc.customerMoney.getCustomerBundleVouchers.useQuery(
        { customerId }
    );

    const columns: ColumnDef<BundleVoucher>[] = [
        {
            id: 'expander',
            header: () => null,
            cell: ({ row }) => (
                <Button
                    variant='ghost'
                    size='sm'
                    className='h-8 w-8 p-0'
                    onClick={() => row.toggleExpanded()}
                >
                    {row.getIsExpanded() ? (
                        <ChevronDown className='h-4 w-4' />
                    ) : (
                        <ChevronRight className='h-4 w-4' />
                    )}
                </Button>
            ),
        },
        {
            accessorKey: 'createdAt',
            header: 'Datum',
            cell: ({ row }) => (
                <span className='text-xs'>
                    {format(new Date(row.original.createdAt), 'dd.MM.yyyy', { locale: de })}
                </span>
            ),
        },
        {
            accessorKey: 'invoiceNumber',
            header: 'Rechnung',
            cell: ({ row }) => (
                <div className='flex flex-col leading-tight'>
                    <span className='font-mono text-xs'>{row.original.invoiceNumber}</span>
                </div>
            ),
        },
        {
            accessorKey: 'sourceBillingDraftNumber',
            header: 'Auftrag',
            cell: ({ row }) => (
                <div className='flex flex-col leading-tight'>
                    <span className='font-mono text-xs'>{row.original.sourceBillingDraftNumber}</span>
                </div>
            ),
        },
        {
            accessorKey: 'treatmentVariantName',
            header: 'Behandlung',
            cell: ({ row }) => (
                <span className='font-medium text-sm'>{row.original.treatmentVariantName}</span>
            ),
        },
        {
            id: 'value',
            header: 'Wert',
            cell: ({ row }) => (
                <span className='font-mono text-sm'>
                    {formatCurrency(row.original.totalValueEur)}
                </span>
            ),
        },
        {
            id: 'sessions',
            header: 'Sitzungen',
            cell: ({ row }) => {
                const { usedUnits, unitQuantity } = row.original;
                return (
                    <span className='font-mono text-sm'>
                        {usedUnits} / {unitQuantity}
                    </span>
                );
            },
        },
        {
            accessorKey: 'remainingValueEur',
            header: 'Restwert',
            cell: ({ row }) => (
                <span className={row.original.remainingUnits > 0 ? 'text-green-600 font-medium text-sm' : 'text-muted-foreground text-sm'}>
                    {formatCurrency(row.original.remainingValueEur)}
                </span>
            ),
        },
        {
            id: 'status',
            header: 'Status',
            cell: ({ row }) => {
                const { usedUnits, remainingUnits, unitQuantity } = row.original;
                if (usedUnits === 0 && remainingUnits === unitQuantity) {
                    return (
                        <Badge variant='outline' className='bg-blue-50 text-blue-700 border-blue-200'>
                            Neu
                        </Badge>
                    );
                }
                if (remainingUnits > 0) {
                    return (
                        <Badge variant='default' className='bg-green-100 text-green-800 hover:bg-green-100'>
                            Aktiv
                        </Badge>
                    );
                }
                return (
                    <Badge variant='outline'>
                        Aufgebraucht
                    </Badge>
                );
            },
        },
    ];

    const table = useReactTable({
        data: vouchers ?? [],
        columns,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getRowCanExpand: () => true,
    });

    if (isLoading) {
        return (
            <div className='space-y-2'>
                {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className='h-12 w-full' />
                ))}
            </div>
        );
    }

    if (!vouchers || vouchers.length === 0) {
        return (
            <div className='text-center py-8 text-muted-foreground'>
                Keine Bundle-Gutscheine vorhanden
            </div>
        );
    }

    return (
        <div className='rounded-md border'>
            <Table>
                <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <TableRow key={headerGroup.id}>
                            {headerGroup.headers.map((header) => (
                                <TableHead key={header.id}>
                                    {header.isPlaceholder
                                        ? null
                                        : flexRender(
                                            header.column.columnDef.header,
                                            header.getContext()
                                        )}
                                </TableHead>
                            ))}
                        </TableRow>
                    ))}
                </TableHeader>
                <TableBody>
                    {table.getRowModel().rows.map((row) => (
                        <>
                            <TableRow
                                key={row.id}
                                className='cursor-pointer hover:bg-muted/50'
                                onClick={() => row.toggleExpanded()}
                            >
                                {row.getVisibleCells().map((cell) => (
                                    <TableCell key={cell.id}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </TableCell>
                                ))}
                            </TableRow>
                            {row.getIsExpanded() && (
                                <TableRow key={`${row.id}-expanded`}>
                                    <TableCell colSpan={columns.length} className='p-0'>
                                        <ExpandedRowContent
                                            voucher={row.original}
                                            customerId={customerId}
                                            isAdmin={isAdmin}
                                        />
                                    </TableCell>
                                </TableRow>
                            )}
                        </>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
};

export default CustomerBundlesTable;
